import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import { useTranslation, Trans } from 'react-i18next';
import '../styles/url-lists.css';

interface URLList {
  id: string;
  display_name: string;
  sites_count: number;
  positively_targeted_count?: number;
  negatively_targeted_count?: number;
  is_locked?: boolean;
}

const URLListsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiser, isAllClients } = useAdvertiser();
  const selectedAdvertiserId = selectedAdvertiser?.advertiser_id || '';
  // State
  const [urlLists, setUrlLists] = useState<URLList[]>([]);
  const [selectedList, setSelectedList] = useState<URLList | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [listName, setListName] = useState('');
  const [sitesText, setSitesText] = useState('');
  const [originalSites, setOriginalSites] = useState<string[]>([]);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load URL lists when advertiser changes
  useEffect(() => {
    if (selectedAdvertiserId) {
      loadUrlLists();
      setSelectedList(null);
      setIsCreating(false);
    }
  }, [selectedAdvertiserId]);

  const loadUrlLists = async () => {
    if (!selectedAdvertiserId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/channels`, {
        params: { advertiser_id: selectedAdvertiserId }
      });
      setUrlLists(response.data.channels || []);
    } catch (err) {
      console.error('Error loading URL lists:', err);
      setUrlLists([]);
    } finally {
      setLoading(false);
    }
  };

  const loadListSites = async (listId: string) => {
    try {
      const response = await api.get(`/api/channels/${listId}/sites`, {
        params: { advertiser_id: selectedAdvertiserId }
      });
      const sites = response.data.sites || [];
      setOriginalSites(sites);
      setSitesText(sites.join('\n'));
    } catch (err) {
      console.error('Error loading sites:', err);
      setSitesText('');
      setOriginalSites([]);
    }
  };

  const handleSelectList = async (list: URLList) => {
    setSelectedList(list);
    setIsCreating(false);
    setListName(list.display_name);
    setError('');
    setSuccess('');
    await loadListSites(list.id);
  };

  const handleCreateNew = () => {
    setSelectedList(null);
    setIsCreating(true);
    setListName('');
    setSitesText('');
    setOriginalSites([]);
    setError('');
    setSuccess('');
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const handleSave = async () => {
    if (!listName.trim()) {
      setError(t('lists.errNameRequired'));
      return;
    }

    const sites = sitesText
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isCreating) {
        // Create new list
        const response = await api.post(`/api/channels/create`, {
          advertiser_id: selectedAdvertiserId,
          display_name: listName.trim(),
          sites: sites
        });

        setSuccess(t('lists.okCreatedUrls', { name: listName, count: response.data.sites_count }));
        await loadUrlLists();

        // Select the newly created list
        const newList = {
          id: response.data.id,
          display_name: listName.trim(),
          sites_count: response.data.sites_count
        };
        setSelectedList(newList);
        setIsCreating(false);
        setOriginalSites(sites);
      } else if (selectedList) {
        // Update existing list
        // Check if name changed
        if (listName.trim() !== selectedList.display_name) {
          await api.patch(`/api/channels/${selectedList.id}`,
            { display_name: listName.trim() },
            { params: { advertiser_id: selectedAdvertiserId } }
          );
        }

        // Update sites
        const response = await api.put(
          `/api/channels/${selectedList.id}/sites`,
          { sites: sites },
          { params: { advertiser_id: selectedAdvertiserId } }
        );

        const validSites = response.data.valid_sites || sites;
        setOriginalSites(validSites);
        setSitesText(validSites.join('\n'));

        setSuccess(response.data.message || t('lists.okUpdated'));
        await loadUrlLists();

        // Update selected list
        setSelectedList({
          ...selectedList,
          display_name: listName.trim(),
          sites_count: validSites.length
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('lists.errSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!selectedList) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedList) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/channels/${selectedList.id}`, {
        params: { advertiser_id: selectedAdvertiserId }
      });
      setShowDeleteModal(false);
      setSuccess(t('lists.okDeleted'));
      setSelectedList(null);
      setIsCreating(false);
      await loadUrlLists();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('lists.errDeleting'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setSelectedList(null);
    setIsCreating(false);
    setListName('');
    setSitesText('');
    setOriginalSites([]);
    setError('');
    setSuccess('');
  };

  // Esc key handler for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDeleteModal && !isDeleting) {
        setShowDeleteModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, isDeleting]);

  // Filter lists by search
  const filteredLists = urlLists.filter(list =>
    list.display_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if there are unsaved changes
  const currentSites = sitesText.split('\n').map(s => s.trim()).filter(s => s.length > 0);
  const hasChanges = isCreating
    ? (listName.trim() !== '' || currentSites.length > 0)
    : (selectedList && (
        listName.trim() !== selectedList.display_name ||
        JSON.stringify(currentSites.sort()) !== JSON.stringify([...originalSites].sort())
      ));

  if (isAllClients) return <ChooseClientPlaceholder pageKey="nav.urlLists" />;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('lists.urlTitle')}</h1>
          <p className="header-subtitle">{t('lists.urlSubtitle')}</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="url-lists-layout">
        {/* Left Panel - Lists */}
        <aside className="url-lists-panel">
          {/* Search */}
          <div className="panel-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('lists.searchLists')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Add Button */}
          {!isAnalyst && (
          <button className="add-list-btn" onClick={handleCreateNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>{t('lists.addList')}</span>
          </button>
          )}

          {/* Lists */}
          <div className="lists-container">
            {loading ? (
              <div className="lists-loading">
                <div className="spinner"></div>
                <span>{t('lists.loadingLists')}</span>
              </div>
            ) : filteredLists.length === 0 ? (
              <div className="lists-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t(searchQuery ? 'lists.noMatching' : 'lists.noUrlLists')}</span>
              </div>
            ) : (
              filteredLists.map(list => (
                <div
                  key={list.id}
                  className={`list-item ${selectedList?.id === list.id ? 'active' : ''}`}
                  onClick={() => handleSelectList(list)}
                >
                  <div className="list-item-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-name" style={list.is_locked ? { color: '#888' } : undefined}>
                      {list.is_locked && (
                        <span title={t('lists.legacyReadOnly')} style={{ marginRight: 6, color: '#888' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                          </svg>
                        </span>
                      )}
                      {list.display_name}
                    </div>
                    <div className="list-item-meta">
                      {t('lists.urlsCount', { count: list.sites_count || 0 })}
                    </div>
                  </div>
                  <svg className="list-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Right Panel - Editor */}
        <div className="url-editor-panel">
          {!selectedList && !isCreating ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{t('lists.selectUrlList')}</h3>
              <p>{t('lists.chooseFromPanel')}</p>
              {!isAnalyst && (
              <button className="editor-empty-btn" onClick={handleCreateNew}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {t('lists.createNewList')}
              </button>
              )}
            </div>
          ) : (
            <div className="editor-content">
              {/* Editor Header */}
              <div className="editor-header">
                <h2>{t(isCreating ? 'lists.newUrlList' : 'lists.editUrlList')}</h2>
                {selectedList?.is_locked && (
                  <span className="editor-locked-badge">{t('lists.serviceManaged')}</span>
                )}
              </div>

              {/* Alerts */}
              {error && (
                <div className="alert alert-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{error}</span>
                  <button onClick={() => setError('')}>&times;</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"></path>
                    <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"></polyline>
                  </svg>
                  <span>{success}</span>
                  <button onClick={() => setSuccess('')}>&times;</button>
                </div>
              )}

              {/* Form */}
              <div className="editor-form">
                <div className="form-group">
                  <label className="form-label">{t('lists.listName')}</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('lists.listNamePh')}
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                    readOnly={!!selectedList?.is_locked}
                  />
                </div>

                <div className="form-group form-group-grow">
                  <label className="form-label">
                    {t('lists.urlsLabel')}
                    <span className="form-label-hint">{t('lists.oneUrlPerLine')}</span>
                  </label>
                  <textarea
                    ref={textareaRef}
                    className="form-textarea"
                    placeholder="example.com&#10;another-site.com&#10;blog.example.org"
                    value={sitesText}
                    onChange={(e) => setSitesText(e.target.value)}
                    readOnly={!!selectedList?.is_locked}
                  />
                  <div className="form-hint">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                    {t('lists.urlHint')}
                  </div>
                </div>

                {/* Stats */}
                <div className="editor-stats">
                  <div className="stat-item">
                    <span className="stat-value">{currentSites.length}</span>
                    <span className="stat-label">{t('lists.urlsEntered')}</span>
                  </div>
                  {!isCreating && selectedList && (
                    <div className="stat-item">
                      <span className="stat-value">{originalSites.length}</span>
                      <span className="stat-label">{t('lists.urlsSaved')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="editor-actions">
                <div className="editor-actions-left">
                  {!isAnalyst && !isCreating && selectedList && !selectedList.is_locked && (() => {
                    const targetedCount = (selectedList.positively_targeted_count || 0) + (selectedList.negatively_targeted_count || 0);
                    const isUsed = targetedCount > 0;
                    return (
                      <div
                        className={`tooltip-wrapper ${isUsed ? 'has-tooltip' : ''}`}
                        data-tooltip={isUsed ? t('lists.usedInGroups', { count: targetedCount }) : undefined}
                      >
                        <button
                          className="btn-danger"
                          onClick={handleDeleteClick}
                          disabled={saving || isUsed}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          {t('common.delete')}
                        </button>
                      </div>
                    );
                  })()}
                </div>
                {!isAnalyst && !selectedList?.is_locked && (
                <div className="editor-actions-right">
                  <button
                    className="btn-secondary"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={saving || !listName.trim()}
                  >
                    {saving ? (
                      <>
                        <div className="btn-spinner"></div>
                        {t('lists.saving')}
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                          <polyline points="17 21 17 13 7 13 7 21"></polyline>
                          <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        {t(isCreating ? 'lists.createList' : 'lists.saveChanges')}
                      </>
                    )}
                  </button>
                </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedList && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('lists.deleteListQ')}</h3>
            <p>
              <Trans
                i18nKey="lists.aboutToDelete"
                values={{ name: selectedList.display_name }}
                components={{ b: <strong /> }}
              /><br/>
              {t('lists.cannotUndo')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('lists.deleting')}
                  </>
                ) : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default URLListsPage;
